import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Chip, Dialog, Container, Skeleton } from "@mui/material";
import { ScrollReveal, StaggerReveal } from './landing/animations';
import { COLORS, STYLES, TYPOGRAPHY, SPACING, TRANSITIONS } from './landing/landingTheme';
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import MemoryIcon from "@mui/icons-material/Memory";
import ChatIcon from "@mui/icons-material/Chat";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

// Estructura de la tabla comparativa — define QUÉ campos mostrar y cómo agruparlos
// Los VALORES vienen de la API
const COMPARISON_CATEGORIES = [
  {
    category: "Capacidad",
    icon: <ChatIcon sx={{ fontSize: 16, color: "#64748b" }} />,
    fields: [
      { key: "num_chatbots", label: "Chatbot con IA" },
      { key: "num_whatsapp", label: "Número WhatsApp" },
      { key: "num_conversaciones", label: "Conversaciones", format: "conversations" },
      { key: "num_tokens", label: "Tokens IA", format: "tokens" },
    ],
  },
  {
    category: "IA y Diagnóstico",
    icon: <PsychologyIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />,
    fields: [
      { key: "tiene_diagnostico_ia", label: "Diagnóstico por IA" },
      { key: "tiene_clasificacion_ia", label: "Clasificación inteligente" },
      { key: "tiene_analisis_imagenes", label: "Análisis de imágenes médicas" },
      { key: "tiene_ocr_documentos", label: "OCR de documentos y recetas" },
    ],
  },
  {
    category: "Gestión Clínica",
    icon: <MedicalServicesIcon sx={{ fontSize: 16, color: "#10b981" }} />,
    fields: [
      { key: "tiene_agente_hibrido", label: "Gestión de citas" },
      { key: "tiene_recordatorios", label: "Recordatorios automáticos" },
      { key: "tiene_emr", label: "Expediente médico (EMR)" },
      { key: "tiene_base_conocimiento", label: "Base de conocimiento" },
    ],
  },
  {
    category: "Memoria e Inteligencia",
    icon: <MemoryIcon sx={{ fontSize: 16, color: "#f59e0b" }} />,
    fields: [
      { key: "dias_memoria", label: "Memoria semántica", format: "days" },
      { key: "tiene_aprendizaje_auto", label: "Aprendizaje automático" },
      { key: "tiene_contexto_sesiones", label: "Contexto entre sesiones" },
    ],
  },
  {
    category: "Analytics y Soporte",
    icon: <AnalyticsIcon sx={{ fontSize: 16, color: "#ec4899" }} />,
    fields: [
      { key: "tiene_dashboard_conv", label: "Dashboard de conversaciones" },
      { key: "tiene_analytics", label: "Analytics avanzados" },
      { key: "tiene_exportacion", label: "Exportación de datos" },
      { key: "soporte", label: "Soporte", format: "support" },
    ],
  },
];

// Formatea un valor de la tabla según el tipo de campo
const formatCellValue = (plan, field) => {
  const value = plan[field.key];

  if (field.format === "conversations") {
    if (plan.es_trial) return `${value}*`;
    return `${value}/mes`;
  }
  if (field.format === "tokens") {
    if (value >= 1000000) {
      const m = value / 1000000;
      return plan.es_trial ? `${m}M*` : `${m}M/mes`;
    }
    if (value >= 1000) return `${Math.round(value / 1000)}K`;
    return String(value);
  }
  if (field.format === "days") {
    return `${value} días`;
  }
  if (field.format === "support") {
    if (plan.tiene_soporte_dedicado) return "Dedicado";
    if (plan.tiene_soporte_prioritario) return "Prioritario";
    return "Email";
  }
  return value;
};

const CellValue = ({ value }) => {
  if (value === true) return <CheckCircleIcon sx={{ fontSize: 17, color: "#10b981" }} />;
  if (value === false) return <CloseIcon sx={{ fontSize: 15, color: "#cbd5e1" }} />;
  return <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>{value}</Typography>;
};

// Plan card component
const PlanCard = ({ plan, onSelect }) => {
  const features = (() => {
    try { return JSON.parse(plan.features_lista || "[]"); }
    catch { return []; }
  })();

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: plan.es_destacado ? "2px solid #f59e0b" : "1px solid #e2e8f0",
        background: "#ffffff",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        transition: TRANSITIONS.normal,
        overflow: "hidden",
        boxShadow: plan.es_destacado
          ? "0 8px 32px rgba(245, 158, 11, 0.15)"
          : "0 2px 8px rgba(0,0,0,0.04)",
        transform: plan.es_destacado ? { xs: "none", md: "scale(1.03)" } : "none",
        zIndex: plan.es_destacado ? 2 : 1,
        "&:hover": {
          transform: plan.es_destacado
            ? { xs: "translateY(-6px)", md: "scale(1.05)" }
            : "translateY(-6px)",
          boxShadow: plan.es_destacado
            ? "0 24px 56px rgba(245, 158, 11, 0.2)"
            : "0 16px 40px rgba(0,0,0,0.08)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: plan.es_destacado ? 4 : 3,
          background: plan.gradiente_plan || plan.color_plan || "#64748b",
        },
      }}
    >
      {plan.es_destacado && (
        <Chip
          icon={<StarIcon sx={{ color: "#0f2440 !important", fontSize: 14 }} />}
          label="Más Popular"
          size="small"
          sx={{
            position: "absolute",
            top: 16, right: 16,
            background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
            color: "#0f2440",
            fontWeight: 700,
            fontSize: 11,
            height: 26,
            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
          }}
        />
      )}

      <Box sx={{ p: { xs: 3, md: 3.5 }, flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: plan.color_plan || "#64748b",
            textTransform: "uppercase",
            letterSpacing: 1.5,
            mb: 1.5,
          }}
        >
          {plan.name}
        </Typography>

        <Box sx={{ mb: 0.5, display: "flex", alignItems: "baseline" }}>
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "2.75rem", md: "3.25rem" },
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1,
              letterSpacing: "-0.03em",
            }}
          >
            {parseFloat(plan.precio_mensual) === 0 ? "Gratis" : `$${Math.round(plan.precio_mensual)}`}
          </Typography>
          {parseFloat(plan.precio_mensual) > 0 && (
            <Typography
              component="span"
              sx={{ fontSize: "0.9rem", color: "#94a3b8", fontWeight: 500, ml: 0.5 }}
            >
              USD/mes
            </Typography>
          )}
        </Box>

        <Typography sx={{ fontSize: "0.85rem", color: "#64748b", mb: 3, lineHeight: 1.5 }}>
          {plan.subtitulo || plan.descripcion || ""}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
          {[
            { label: "conversaciones", value: plan.es_trial ? `${plan.num_conversaciones}*` : String(plan.num_conversaciones) },
            { label: "tokens IA", value: plan.num_tokens >= 1000000 ? `${plan.num_tokens / 1000000}M` : `${Math.round(plan.num_tokens / 1000)}K` },
          ].map((metric, mi) => (
            <Box
              key={mi}
              sx={{
                px: 1.2, py: 0.5,
                borderRadius: 2,
                background: plan.es_destacado ? `${plan.color_plan}08` : "#f8fafc",
                border: `1px solid ${plan.es_destacado ? `${plan.color_plan}20` : "#e2e8f0"}`,
              }}
            >
              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                <Box component="span" sx={{ fontWeight: 700, color: plan.es_destacado ? plan.color_plan : "#0f172a" }}>
                  {metric.value}
                </Box>{" "}
                {metric.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ height: 1, background: "#f1f5f9", mb: 2.5 }} />

        {plan.hereda_de && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8, mb: 2,
              px: 1.2, py: 0.6,
              borderRadius: 2,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              width: "fit-content",
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 14, color: "#10b981" }} />
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#15803d" }}>
              Todo lo del plan {plan.hereda_de}, más:
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 3, flex: 1 }}>
          {features.map((feature, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <CheckCircleIcon
                sx={{ color: plan.es_destacado ? "#f59e0b" : "#10b981", fontSize: 16, mr: 1, flexShrink: 0 }}
              />
              <Typography sx={{ fontSize: "0.85rem", color: "#334155", lineHeight: 1.4 }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          {...(plan.es_trial
            ? {
                component: "a",
                href: "https://wa.me/573052608968?text=Hola%2C%20me%20interesa%20una%20demo%20de%20NARA%20para%20mi%20consultorio",
                target: "_blank",
                rel: "noopener",
              }
            : {
                onClick: () => onSelect(plan.name),
              }
          )}
          sx={{
            py: 1.3,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: "0.9rem",
            textTransform: "none",
            textDecoration: "none",
            transition: TRANSITIONS.normal,
            ...(plan.es_destacado
              ? {
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "#0f2440",
                  boxShadow: "0 4px 16px rgba(245, 158, 11, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 8px 24px rgba(245, 158, 11, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }
              : {
                  background: "#f1f5f9",
                  color: "#1e3a5f",
                  boxShadow: "none",
                  "&:hover": {
                    background: "#e2e8f0",
                    transform: "translateY(-1px)",
                  },
                }),
          }}
        >
          {plan.texto_cta || "Elegir"}
        </Button>
      </Box>
    </Box>
  );
};

// Skeleton loader while plans are fetched
const PlanCardSkeleton = () => (
  <Box sx={{ borderRadius: 4, border: "1px solid #e2e8f0", background: "#fff", p: 3.5 }}>
    <Skeleton variant="text" width={60} height={20} sx={{ mb: 1.5 }} />
    <Skeleton variant="text" width={120} height={50} sx={{ mb: 0.5 }} />
    <Skeleton variant="text" width={200} height={20} sx={{ mb: 3 }} />
    <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
      <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" width={80} height={28} sx={{ borderRadius: 2 }} />
    </Box>
    <Box sx={{ height: 1, background: "#f1f5f9", mb: 2.5 }} />
    {[1, 2, 3, 4, 5].map(i => (
      <Skeleton key={i} variant="text" width="80%" height={22} sx={{ mb: 0.8 }} />
    ))}
    <Skeleton variant="rounded" height={44} sx={{ mt: 2, borderRadius: 2.5 }} />
  </Box>
);

export default function PricingPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch(`${API_BASE}/plans/`);
        if (!res.ok) throw new Error("Failed to fetch plans");
        const data = await res.json();
        // Only active plans, sorted by orden
        const active = data.filter((p) => p.activo);
        const sorted = active.sort((a, b) => (a.orden || 0) - (b.orden || 0));
        setPlans(sorted);
      } catch (err) {
        console.error("Error fetching plans:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSelectPlan = (planName) => {
    setSelectedPlan(planName);
    setOpenModal(true);
  };

  return (
    <Box
      sx={{
        py: SPACING.sectionY,
        position: "relative",
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <ScrollReveal>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                color: COLORS.accent,
                fontSize: TYPOGRAPHY.tiny,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                mb: 1.5,
              }}
            >
              Planes
            </Typography>
            <Typography variant="h2" sx={{ ...STYLES.sectionTitle }}>
              Un plan para cada etapa de tu consultorio
            </Typography>
            <Typography sx={{ ...STYLES.sectionSubtitle }}>
              Todos los planes incluyen IA de última generación.
              Sin contratos, cancela cuando quieras.
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Plan Cards */}
        {loading ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: { xs: 3, md: 4 },
              mb: { xs: 6, md: 8 },
            }}
          >
            {[1, 2, 3].map(i => <PlanCardSkeleton key={i} />)}
          </Box>
        ) : plans.length > 0 ? (
          <StaggerReveal
            staggerDelay={0.12}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: `repeat(${Math.min(plans.length, 3)}, 1fr)` },
              gap: { xs: 3, md: 4 },
              mb: { xs: 6, md: 8 },
              alignItems: { md: "center" },
            }}
          >
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} onSelect={handleSelectPlan} />
            ))}
          </StaggerReveal>
        ) : null}

        {/* Comparison Table */}
        {!loading && plans.length > 0 && (
          <ScrollReveal>
            <Box sx={{ maxWidth: 900, mx: "auto" }}>
              <Typography
                align="center"
                sx={{
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  fontWeight: 700,
                  color: "#0f172a",
                  mb: 0.5,
                }}
              >
                Comparativa detallada
              </Typography>
              <Typography
                align="center"
                sx={{ fontSize: "0.88rem", color: "#64748b", mb: 4 }}
              >
                Todas las funcionalidades lado a lado
              </Typography>

              <Box
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                }}
              >
                {/* Table Header */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: `1.8fr repeat(${plans.length}, 1fr)`,
                      md: `2.2fr repeat(${plans.length}, 1fr)`,
                    },
                    background: "#ffffff",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                      Funcionalidad
                    </Typography>
                  </Box>
                  {plans.map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        p: { xs: 1.5, md: 2 },
                        textAlign: "center",
                        borderLeft: "1px solid #e2e8f0",
                        ...(p.es_destacado && {
                          background: "rgba(245, 158, 11, 0.04)",
                        }),
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: p.color_plan || "#64748b" }}>
                        {p.name}
                      </Typography>
                      {parseFloat(p.precio_mensual) > 0 && (
                        <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                          ${Math.round(p.precio_mensual)}/mes
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Table Body */}
                {COMPARISON_CATEGORIES.map((group, gi) => (
                  <Box key={gi}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: { xs: 1.5, md: 2 },
                        py: 1,
                        background: "#ffffff",
                        borderBottom: "1px solid #e2e8f0",
                        ...(gi > 0 && { borderTop: "1px solid #e2e8f0" }),
                      }}
                    >
                      {group.icon}
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: "#0f172a",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {group.category}
                      </Typography>
                    </Box>

                    {group.fields.map((field, fi) => (
                      <Box
                        key={fi}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: `1.8fr repeat(${plans.length}, 1fr)`,
                            md: `2.2fr repeat(${plans.length}, 1fr)`,
                          },
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background 0.15s",
                          "&:hover": { background: "#ffffff" },
                        }}
                      >
                        <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.1, display: "flex", alignItems: "center" }}>
                          <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
                            {field.label}
                          </Typography>
                        </Box>
                        {plans.map((plan) => (
                          <Box
                            key={plan.id}
                            sx={{
                              px: { xs: 0.5, md: 2 },
                              py: 1.1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderLeft: "1px solid #f1f5f9",
                              ...(plan.es_destacado && {
                                background: "rgba(245, 158, 11, 0.02)",
                              }),
                            }}
                          >
                            <CellValue value={formatCellValue(plan, field)} />
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                ))}

                {/* Table Footer - CTA row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: `1.8fr repeat(${plans.length}, 1fr)`,
                      md: `2.2fr repeat(${plans.length}, 1fr)`,
                    },
                    borderTop: "1px solid #e2e8f0",
                    background: "#ffffff",
                  }}
                >
                  <Box sx={{ p: 1.5 }} />
                  {plans.map((plan) => (
                    <Box
                      key={plan.id}
                      sx={{
                        p: { xs: 1, md: 1.5 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderLeft: "1px solid #e2e8f0",
                        ...(plan.es_destacado && {
                          background: "rgba(245, 158, 11, 0.04)",
                        }),
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        {...(plan.es_trial
                          ? {
                              component: "a",
                              href: "https://wa.me/573052608968?text=Hola%2C%20me%20interesa%20una%20demo%20de%20NARA%20para%20mi%20consultorio",
                              target: "_blank",
                              rel: "noopener",
                            }
                          : {
                              onClick: () => handleSelectPlan(plan.name),
                            }
                        )}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "none",
                          textDecoration: "none",
                          borderRadius: 2,
                          px: { xs: 1.5, md: 2.5 },
                          py: 0.6,
                          ...(plan.es_destacado
                            ? {
                                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                color: "#0f2440",
                                boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                                "&:hover": {
                                  background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                                },
                              }
                            : {
                                background: "#f1f5f9",
                                color: "#1e3a5f",
                                boxShadow: "none",
                                "&:hover": { background: "#e2e8f0" },
                              }),
                        }}
                      >
                        {plan.es_trial ? "Gratis" : "Elegir"}
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </ScrollReveal>
        )}

        {/* Bottom note */}
        <Typography
          align="center"
          sx={{ fontSize: "0.8rem", color: "#94a3b8", mt: 4 }}
        >
          Todos los precios en USD. Facturación mensual.
          Puedes cambiar o cancelar tu plan en cualquier momento.
        </Typography>
      </Container>

      {/* Coming Soon Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box sx={{ p: 4, textAlign: "center", background: "#ffffff" }}>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", mb: 1 }}>
            Plan {selectedPlan}
          </Typography>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#f59e0b", mb: 1 }}>
            Disponible próximamente
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#64748b", mb: 3 }}>
            Estamos trabajando para ofrecerte este plan muy pronto.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenModal(false)}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#0f2440",
              borderRadius: 2,
              fontWeight: 700,
              px: 4, py: 1,
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(245, 158, 11, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
            }}
          >
            Cerrar
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
